// client/src/services/publishingApi.js
// Publishing tools (Roadmap Phase 3): best times, AI captions, bulk CSV
// scheduling and RSS feeds (server/routes/publishingRoutes.js at
// /api/publishing/:tenant), plus Google Drive / Canva media import
// (server/routes/integrations/googleDriveRoutes.js, canvaRoutes.js).
// The server checks :tenant against the session on every call.

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';

async function request(method, url, body) {
  const res = await fetch(`${BASE_URL}${url}`, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || data.error || `${res.status} ${res.statusText}`);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

const pub = (tenantId) => `/api/publishing/${Number(tenantId)}`;

/** Message from a failed call, or the fallback. */
export function errorText(err, fallback) {
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}

// ---- best time to post
/** { timezone, platforms, source: default|blended|history, basis, slots: [{date, time, utc, score}], usual } */
export const getBestTimes = (tenantId, platforms = []) =>
  request('GET', `${pub(tenantId)}/best-times?platforms=${encodeURIComponent(platforms.join(','))}`);

// ---- AI caption assistant
/** { enabled, reason?, usage?: { used, limit, unlimited } } */
export const getAiStatus = (tenantId) => request('GET', `${pub(tenantId)}/ai/status`);
/** body: { action: draft|shorten|adapt|hashtags, text?, brief?, platform?, tone?, language?, eventId? } */
export const aiCaption = (tenantId, body) => request('POST', `${pub(tenantId)}/ai/caption`, body);

// ---- bulk CSV
export const bulkTemplateUrl = (tenantId) => `${BASE_URL}${pub(tenantId)}/bulk/template.csv`;
export const validateBulk = (tenantId, csv) => request('POST', `${pub(tenantId)}/bulk/validate`, { csv });
export const commitBulk = (tenantId, csv, skipInvalid) => request('POST', `${pub(tenantId)}/bulk/commit`, { csv, skipInvalid: Boolean(skipInvalid) });

// ---- RSS feeds
export const listFeeds = (tenantId) => request('GET', `${pub(tenantId)}/rss`);
export const previewFeed = (tenantId, body) => request('POST', `${pub(tenantId)}/rss/preview`, body);
export const addFeed = (tenantId, body) => request('POST', `${pub(tenantId)}/rss`, body);
export const updateFeed = (tenantId, id, body) => request('PUT', `${pub(tenantId)}/rss/${Number(id)}`, body);
export const removeFeed = (tenantId, id) => request('DELETE', `${pub(tenantId)}/rss/${Number(id)}`);
export const feedItems = (tenantId, id) => request('GET', `${pub(tenantId)}/rss/${Number(id)}/items`);

// ---- media import
const drive = (tenantId) => `/api/integrations/google-drive/${Number(tenantId)}`;
const canva = (tenantId) => `/api/integrations/canva/${Number(tenantId)}`;
export const driveLoginUrl = (tenantId) => `${BASE_URL}/api/integrations/google-drive/login?tenantId=${Number(tenantId)}`;
export const canvaLoginUrl = (tenantId) => `${BASE_URL}/api/integrations/canva/login?tenantId=${Number(tenantId)}`;
export const getDriveStatus = (tenantId) => request('GET', `${drive(tenantId)}/status`);
export const getDrivePickerConfig = (tenantId) => request('GET', `${drive(tenantId)}/picker-config`);
export const importFromDrive = (tenantId, fileIds, eventId) => request('POST', `${drive(tenantId)}/import`, { fileIds, eventId });
export const disconnectDrive = (tenantId) => request('DELETE', `${drive(tenantId)}/connection`);
export const getCanvaStatus = (tenantId) => request('GET', `${canva(tenantId)}/status`);
export const listCanvaDesigns = (tenantId, { query = '', continuation = '' } = {}) =>
  request('GET', `${canva(tenantId)}/designs?query=${encodeURIComponent(query)}&continuation=${encodeURIComponent(continuation)}`);
export const importFromCanva = (tenantId, body) => request('POST', `${canva(tenantId)}/import`, body);
export const disconnectCanva = (tenantId) => request('DELETE', `${canva(tenantId)}/connection`);
